import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Spinner, Alert, Table, Button } from 'react-bootstrap';
import axios from '../../plugins/axios';
import { getMerchantDetails, merchantTerminals } from '../../plugins/urls';
import Layout from '../../../../src/components/Layout';
import PosDeviceDetails from '../PosComponents/PosDeviceDetails';

const MerchantDetails = () => {
  const { merchantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedTerminalId, setSelectedTerminalId] = useState(null);

  useEffect(() => {
    const fetchMerchantData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch merchant details
        const merchantRes = await axios.get(getMerchantDetails(merchantId));
        if (merchantRes.data && merchantRes.data.success) {
          const details = merchantRes.data.data;
          setMerchantDetails(details);

          // Fetch merchant terminals using merchantId from details
          if (details.merchantId) {
            const terminalsRes = await axios.get(merchantTerminals(details.merchantId));
            if (terminalsRes.data && terminalsRes.data.success) {
              setTerminals(terminalsRes.data.data.content || []);
            } else {
              setError(terminalsRes.data.message || 'Failed to fetch terminals.');
            }
          }
        } else {
          setError(merchantRes.data.message || 'Failed to fetch merchant details.');
        }
      } catch (err) {
        console.error('Error fetching merchant data:', err);
        setError('An error occurred while fetching merchant data.');
      } finally {
        setLoading(false);
      }
    };

    if (merchantId) {
      fetchMerchantData();
    }
  }, [merchantId]);

  const handleViewMoreTerminal = (terminal) => {
    setSelectedTerminalId(terminal.terminalId);
    setDetailsOpen(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!merchantDetails) {
    return <Alert variant="info">No merchant details found.</Alert>;
  }

  return (
    <Layout title="Merchant Details">
    <div className="merchant-details-page p-3">
      <Button variant="secondary" onClick={() => window.history.back()} className="mb-3">
        &larr; Back to Users
      </Button>
      <Card className="mb-4">
        <Card.Header as="h5">Merchant Details: {merchantDetails.orgName || merchantDetails.firstname + ' ' + merchantDetails.surname}</Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive>
            <tbody>
              <tr><td>**Merchant ID:**</td><td>{merchantDetails.merchantId}</td></tr>
              <tr><td>**User ID:**</td><td>{merchantDetails.userId}</td></tr>
              <tr><td>**First Name:**</td><td>{merchantDetails.firstname}</td></tr>
              <tr><td>**Last Name:**</td><td>{merchantDetails.surname}</td></tr>
              <tr><td>**Email:**</td><td>{merchantDetails.email}</td></tr>
              <tr><td>**Phone Number:**</td><td>{merchantDetails.phoneNumber}</td></tr>
              <tr><td>**Organization Name:**</td><td>{merchantDetails.orgName || 'N/A'}</td></tr>
              <tr><td>**Business Type:**</td><td>{merchantDetails.businessType || 'N/A'}</td></tr>
              <tr><td>**State:**</td><td>{merchantDetails.state || 'N/A'}</td></tr>
              <tr><td>**City:**</td><td>{merchantDetails.city || 'N/A'}</td></tr>
              <tr><td>**Office Address:**</td><td>{merchantDetails.officeAddress || 'N/A'}</td></tr>
              <tr><td>**Active:**</td><td>{merchantDetails.active ? 'Yes' : 'No'}</td></tr>
              <tr><td>**Created At:**</td><td>{new Date(merchantDetails.createdAt).toLocaleString()}</td></tr>
              {/* Add more details as needed based on the API response */}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header as="h5">Terminals</Card.Header>
        <Card.Body>
          {terminals.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Terminal Name</th>
                  <th>Terminal ID</th>
                  <th>Serial Number</th>
                  <th>POS Account No</th>
                  <th>Status</th>
                  <th>Issued Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {terminals.map((terminal) => (
                  <tr key={terminal.id}>
                    <td>{terminal.terminalName}</td>
                    <td>{terminal.terminalId}</td>
                    <td>{terminal.terminalSerialNumber}</td>
                    <td>{terminal.posAccountNumber || 'N/A'}</td>
                    <td>{terminal.status}</td>
                    <td>{new Date(terminal.issuedDate).toLocaleString()}</td>
                    <td>
                      <Button
                        variant="#ff6700"
                        style={{ backgroundColor: '#ff6700', border: 'none' }}
                        size="sm"
                        onClick={() => handleViewMoreTerminal(terminal)}
                      >
                        View More
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info">No terminals found for this merchant.</Alert>
          )}
        </Card.Body>
      </Card>
      <PosDeviceDetails 
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        terminalId={selectedTerminalId}
      />
    </div>
    </Layout>
  );
};

export default MerchantDetails;
